import { FC, useCallback } from 'react';
import { format } from 'date-fns';
import { SoftwareUpdate, StatusReport } from 'models/softwareUpate';
import IconCircle from 'components/UI/IconCircle';
import Tooltip from 'components/UI/Tooltip';
import { HeaderCell } from 'components/UI/Table/SortableTable';
import { APIError } from 'models';
import TableBody from 'components/UI/Table/TableBody';
import TableRow from 'components/UI/Table/TableRow';
import TableBodyCell from 'components/UI/Table/TableBodyCell';
import Table from 'components/UI/Table';
import TableHeader from 'components/UI/Table/TableHeader';
import TableHeaderCell from 'components/UI/Table/TableHeaderCell';
import { FormattedMessage } from 'react-intl';
import TableDataCell from 'components/UI/Table/TableDataCell';
import ArrowIcon from 'components/UI/Icon/ArrowIcon';
import Cable from 'assets/vectors/cable.svg';
import { Image } from './styled';

const headerCells: HeaderCell[] = [
  {
    title: 'connectivity.software_history_software',
    sortable: false,
  },
  {
    title: 'connectivity.software_history_date',
    sortable: false,
  },
  {
    title: 'connectivity.status',
    sortable: false,
  },
];

type Props = {
  softwareUpdates?: SoftwareUpdate[] | null;
  error?: APIError | null;
  onClickRow: (softwareUpdate: SoftwareUpdate | undefined) => void;
};

function getDisplayStatus(
  status: string,
  detailStatus: string,
  statusReports: StatusReport[]
) {
  // we had a bug in ESLM that set status to retrieved every time we checked if software update existed
  // this caused that status was set to retrieved after finished
  // filter out the retrieved status reports and get the latest
  const statusReportErrorOrFinished = statusReports.find(
    (report) => report.type === 'finished' || report.type === 'error'
  );
  if (detailStatus === 'retrieved' && statusReportErrorOrFinished) {
    return statusReportErrorOrFinished.type;
  }
  return status !== 'pending' ? detailStatus : status;
}

const SoftwareHistoryTable: FC<Props> = ({ softwareUpdates, onClickRow }) => {
  const renderTooltip = (text: string) => {
    if (text?.length > 15) {
      return (
        <Tooltip paragraph={text}>
          {text.substring(0, 15).concat('...')}
        </Tooltip>
      );
    }
    return text;
  };

  // Render status
  const renderStatus = useCallback(
    (
      status: string,
      detailStatus: string,
      message: string,
      statusReports: StatusReport[],
      type: string
    ) => {
      if (type === 'softwarehistory') {
        return <Image src={Cable} alt="cable" />;
      }
      const finishedWithErrors =
        status === 'finished' && detailStatus === 'error';
      const iconStatus = getDisplayStatus(status, detailStatus, statusReports);
      const iconText = finishedWithErrors ? message : iconStatus;
      return <IconCircle status={iconStatus} text={iconText} size="16px" />;
    },
    []
  );

  // on click
  const onClick = (id: string) => {
    const softwareUpdate = softwareUpdates?.find((update) => update.id === id);
    onClickRow(softwareUpdate);
  };

  if (!softwareUpdates || softwareUpdates.length === 0) {
    return null;
  }

  // Table rows
  const bodyRows = softwareUpdates.map(({ id, type, attributes }) => {
    const {
      software,
      status,
      updateType,
      statusMessage,
      detailStatus,
      statusReports,
    } = attributes;
    const lastestUpdate =
      statusReports && statusReports[0] ? statusReports[0].reportedAt : 0;
    console.log(
      `Status: ${status} detailStatus: ${detailStatus} updateType: ${updateType}`
    );
    return {
      software: renderTooltip(software),
      lastModified:
        lastestUpdate > 0
          ? format(Number(lastestUpdate), 'yyyy-MM-dd HH:mm:ss')
          : '-',
      status: renderStatus(
        status,
        detailStatus,
        statusMessage,
        statusReports,
        type
      ),
      link: id,
    };
  });

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {headerCells.map((cell, index) => {
            return (
              <TableHeaderCell key={index}>
                {cell.title && <FormattedMessage id={cell.title} />}
              </TableHeaderCell>
            );
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {bodyRows.map((item, rowIndex) => {
          return (
            <TableRow key={rowIndex}>
              {Object.entries(item).map(([name, value], colIndex) => (
                <TableBodyCell key={colIndex} name={name} value={value} />
              ))}
              <TableDataCell onClick={() => onClick(item.link)} isPressable>
                <ArrowIcon
                  size="small"
                  direction="right"
                  transition={false}
                  themeType="grey6"
                />
              </TableDataCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default SoftwareHistoryTable;
